import React, { useEffect, useState } from 'react';
import { BaseCard } from './BaseCard';
import { PrimaryButton } from '../Buttons/PrimaryButton';
import { SecondaryButton } from '../Buttons/SecondaryButton';
import { DangerButton } from '../Buttons/DangerButton';
import { useBoundStore } from '../../Store/store';
import { ExceptionRateDialog } from '../Dialogs/Contracts/ExceptionRateDialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import {DeleteConfirmDialog} from "../Dialogs/Confirmation/DeleteConfirmDialog";
import {isMyCcpSubdomain} from "../../Utils/SubdomainHelper";
import {useTranslation} from "react-i18next";
export const ExceptionRatesCard = ({ freelancerId }) => {
    const {t} = useTranslation();
    const getExceptionRatesForFreelancer = useBoundStore((state) => state.getExceptionRatesForFreelancer);
    const deleteExceptionRate = useBoundStore((state) => state.deleteExceptionRate);
    const exceptionRates = useBoundStore((state) => state.exceptionRates);
    const [isLoading, setIsLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedExceptionRate, setSelectedExceptionRate] = useState(null);
    const [exceptionRateToDelete, setExceptionRateToDelete] = useState(null);
    useEffect(() => {
        const fetchExceptionRates = async () => {
            setIsLoading(true);
            await getExceptionRatesForFreelancer(freelancerId);
            setIsLoading(false);
        };
        if (freelancerId) {
            fetchExceptionRates();
        }
    }, [freelancerId, getExceptionRatesForFreelancer]);
    const handleAddNew = () => {
        setSelectedExceptionRate(null);
        setOpenDialog(true);
    };
    const handleEdit = (exceptionRate) => {
        setSelectedExceptionRate(exceptionRate);
        setOpenDialog(true);
    };
    const handleDelete = (exceptionRate) => {
        setExceptionRateToDelete(exceptionRate);
        setOpenDeleteDialog(true);
    };
    const confirmDelete = async () => {
        if (exceptionRateToDelete) {
            await deleteExceptionRate(freelancerId, exceptionRateToDelete.id);
            setOpenDeleteDialog(false);
            setExceptionRateToDelete(null);
        }
    };
    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedExceptionRate(null);
    };
    if (isLoading) {
        return (
            <BaseCard border={false}>
                <h2 className="text-lg text-gray-700 uppercase mb-4">{t('exception_rates.title')}</h2>
                <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
            </BaseCard>
        );
    }
    return (
        <>
            <BaseCard border={false}>
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h2 className="text-lg text-gray-700 uppercase mb-1 mt-2">{t('exception_rates.title')}</h2>
                        <p className="text-sm text-gray-500">
                            {t('exception_rates.description')}
                        </p>
                    </div>
                    <div>
                        <PrimaryButton onClick={handleAddNew}>
                            <FontAwesomeIcon icon={faPlus} className="mr-2" />
                            {t('exception_rates.add_new')}
                        </PrimaryButton>
                    </div>
                </div>
                {exceptionRates && exceptionRates.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('tables.branch')}
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('exception_rates.internal_rate')}
                                    </th>
                                    {!isMyCcpSubdomain() && <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('exception_rates.external_rate')}
                                    </th>}
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('exception_rates.period')}
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('tables.status')}
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('tables.actions')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {exceptionRates.map((rate) => {
                                    const now = dayjs();
                                    const startDate = dayjs(rate.start_date);
                                    const endDate = rate.end_date ? dayjs(rate.end_date) : null;
                                    const isActive = endDate ? (now.isAfter(startDate) && now.isBefore(endDate)) : now.isAfter(startDate);
                                    const isPending = now.isBefore(startDate);
                                    const isExpired = endDate ? now.isAfter(endDate) : false;
                                    const isExpiringSoon = endDate ? (endDate.diff(now, 'day') <= 7 && endDate.diff(now, 'day') > 0) : false;

                                    return (
                                        <tr key={rate.id} className={isExpired ? 'bg-gray-50 opacity-60' : ''}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {rate.branch?.company_name || t('general.labels.unknown')}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                €{parseFloat(rate.internal_rate).toFixed(2)}
                                            </td>
                                            {!isMyCcpSubdomain() && <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                €{(parseFloat(rate.internal_rate) * 1.18).toFixed(2)}
                                            </td>}
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {dayjs(rate.start_date).format('DD/MM/YYYY')} - {endDate ? endDate.format('DD/MM/YYYY') : t('exception_rates.indefinite')}
                                                {isExpiringSoon && (
                                                    <div className="text-xs text-orange-600 mt-1">
                                                        {t('exception_rates.expires_in', {days: endDate.diff(now, 'day')})}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {isActive && !isExpiringSoon && (
                                                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                        {t('exception_rates.status_active')}
                                                    </span>
                                                )}
                                                {isActive && isExpiringSoon && (
                                                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800">
                                                        {t('exception_rates.status_expiring_soon')}
                                                    </span>
                                                )}
                                                {isPending && (
                                                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                                        {t('exception_rates.status_pending')}
                                                    </span>
                                                )}
                                                {isExpired && (
                                                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                                        {t('exception_rates.status_expired')}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        onClick={() => handleEdit(rate)}
                                                        className="text-primary hover:text-primary-700 transition-colors"
                                                        title={t('general.actions.edit')}
                                                    >
                                                        <FontAwesomeIcon icon={faEdit} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(rate)}
                                                        className="text-red-600 hover:text-red-800 transition-colors"
                                                        title={t('general.actions.delete')}
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <p className="text-gray-500 mb-4">{t('exception_rates.no_results')}</p>
                        <SecondaryButton onClick={handleAddNew}>
                            <FontAwesomeIcon icon={faPlus} className="mr-2" />
                            {t('exception_rates.add_first')}
                        </SecondaryButton>
                    </div>
                )}
            </BaseCard>
            <ExceptionRateDialog
                open={openDialog}
                onClose={handleCloseDialog}
                freelancerId={freelancerId}
                exceptionRate={selectedExceptionRate}
            />
            <DeleteConfirmDialog
                open={openDeleteDialog}
                onClose={() => {
                    setOpenDeleteDialog(false);
                    setExceptionRateToDelete(null);
                }}
                onConfirm={confirmDelete}
            />
        </>
    );
};
